import { create } from 'zustand';

// ── Types ───────────────────────────────────────────────────────────────────

export interface BEOJobEntry {
  jobId: string;
  fileName: string;
  status: 'queued' | 'processing' | 'done' | 'error';
  heartbeatMessage: string | null;
  lastHeartbeat: string | null;
  eventCount: number | null;
  errorMessage: string | null;
  quieted: boolean;
}

interface BEOImportJobsState {
  jobs: BEOJobEntry[];
  addJob: (jobId: string, fileName: string) => void;
  updateJob: (jobId: string, patch: Partial<BEOJobEntry>) => void;
  dismissJob: (jobId: string) => void;
  quietAll: () => void;
  reset: () => void;
}

// ── Session storage persistence ─────────────────────────────────────────────

const STORAGE_KEY = 'beoImport.jobs';

function loadFromStorage(): BEOJobEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToStorage(jobs: BEOJobEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch { /* quota exceeded — ignore */ }
}

// ── Store ───────────────────────────────────────────────────────────────────

const useBEOImportJobsStore = create<BEOImportJobsState>((set) => ({
  jobs: loadFromStorage(),

  addJob: (jobId, fileName) =>
    set((state) => {
      // Avoid duplicates
      if (state.jobs.some((j) => j.jobId === jobId)) return state;
      const entry: BEOJobEntry = {
        jobId,
        fileName,
        status: 'queued',
        heartbeatMessage: null,
        lastHeartbeat: null,
        eventCount: null,
        errorMessage: null,
        quieted: false,
      };
      const next = [...state.jobs, entry];
      saveToStorage(next);
      return { jobs: next };
    }),

  updateJob: (jobId, patch) =>
    set((state) => {
      const next = state.jobs.map((j) =>
        j.jobId === jobId ? { ...j, ...patch } : j,
      );
      saveToStorage(next);
      return { jobs: next };
    }),

  dismissJob: (jobId) =>
    set((state) => {
      const next = state.jobs.filter((j) => j.jobId !== jobId);
      saveToStorage(next);
      return { jobs: next };
    }),

  quietAll: () =>
    set((state) => {
      const next = state.jobs.map((j) => ({ ...j, quieted: true }));
      saveToStorage(next);
      return { jobs: next };
    }),

  reset: () => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* best effort */ }
    set({ jobs: [] });
  },
}));

export default useBEOImportJobsStore;
